import React, {
  ForwardedRef,
  FocusEvent,
  ChangeEvent,
  memo,
  MouseEvent,
  KeyboardEvent
} from 'react';
import cl from 'classnames';

import { I18nText } from '../../../../types';

import { useTranslate } from '../../../../common/hooks/useTranslate';

import { Translate } from '../../../../helpers/Translate';
import { Icon } from '../../../../helpers/Icon';

import { IconsEnum } from '../../../../assets/icons/types';

interface ProfileInputFieldProps {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  error?: string | null;
  errorClassName?: string;
  id?: string;
  inputClassName?: string;
  inputWrapperClassName?: string;
  label?: string;
  i18nLabel?: I18nText;
  i18nPlaceholder?: I18nText;
  labelWrapperClassName?: string;
  max?: number;
  min?: number;
  name: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  showError?: boolean;
  step?: number | 'any';
  type?: string;
  value?: string;
  prefix?: string;
  suffix?: string;
  readOnly?: boolean;
  autoComplete?: string;
  rightIcon?: IconsEnum;
}

const ProfileInputField = React.forwardRef<
  HTMLInputElement,
  ProfileInputFieldProps
>(
  (
    {
      autoFocus = false,
      className,
      disabled,
      error,
      errorClassName,
      id,
      inputClassName,
      inputWrapperClassName,
      label,
      i18nLabel,
      labelWrapperClassName,
      max,
      min,
      name,
      placeholder,
      i18nPlaceholder,
      onBlur,
      onChange,
      onClick,
      onKeyDown,
      required,
      showError = true,
      step,
      type,
      value,
      prefix,
      suffix,
      readOnly = false,
      autoComplete,
      rightIcon
    }: ProfileInputFieldProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const { t } = useTranslate();

    return (
      <div className={className}>
        {i18nLabel || label ? (
          <div className={labelWrapperClassName}>
            <label
              htmlFor={id || name}
              className="block py-2 pl-3 sm:pl-0 pr-2 leading-none text-gray-600 dark:text-gray-400 w-full text-sm sm:text-base"
            >
              {i18nLabel ? <Translate id={i18nLabel} /> : label}
            </label>
          </div>
        ) : null}

        <div className="flex-1">
          <div className={inputWrapperClassName || 'relative flex-1'}>
            <input
              autoFocus={autoFocus}
              className={cl(
                inputClassName ||
                  'leading-none border-0 text-sm lg:text-base py-2 px-3 w-full rounded placeholder:italic',
                {
                  'bg-red-100 text-red-900 placeholder-red-300 dark:bg-red-900 dark:placeholder-red-600 dark:text-red-500 focus:border-none focus:ring-transparent':
                    error,
                  'pl-6': prefix,
                  'pr-6': suffix || rightIcon
                }
              )}
              disabled={disabled}
              id={id || name}
              max={max}
              min={min}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              onClick={onClick}
              onKeyDown={onKeyDown}
              placeholder={i18nPlaceholder ? t(i18nPlaceholder) : placeholder}
              ref={ref}
              required={required}
              step={step}
              type={type}
              value={value}
              readOnly={readOnly}
              autoComplete={autoComplete}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {prefix && (
                <span className="text-gray-500 sm:text-sm">{prefix}</span>
              )}
            </div>

            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {suffix && (
                <span className="text-gray-500 sm:text-sm">{suffix}</span>
              )}

              {rightIcon && (
                <Icon icon={rightIcon} className="h-4 w-4 text-gray-500" />
              )}
            </div>
          </div>
          {error && showError && (
            <p className={cl(errorClassName || 'mt-2 text-sm text-red-600')}>
              {/^forms\.errors+/.test(error) ? <Translate id={error} /> : error}
            </p>
          )}
        </div>
      </div>
    );
  }
);

ProfileInputField.displayName = 'ProfileInputField';

export default memo(ProfileInputField);
